import { actions as ACTION } from "./types";
import { Api, ApiUrl, pageApi } from "@/services";
import store from "../index";

export const getThemeId = async () => {
  const themeRes = await pageApi.getThemeApi();
  return themeRes?.data?.themes?.edges?.[0]?.node?.id;
};

const PAGE_TYPES = {
  productpages: "GP_PRODUCT",
  articlepages: "GP_ARTICLE"
};

const getPublishedPageByVersion = (shopType, params) => {
  if (shopType.isGemPagesV7) {
    return getPublishedPageV7(params)
  } else if (shopType.isGemPagesV6) {
    return getPublishedPageV6(params)
  } else if (shopType.isGemPagesMixed) {
    return getPublishedPageMixed(params)
  }
}

const unPublishedPageByVersion = (shopType, params) => {
  if (shopType.isGemPagesV7) {
    return unPublishPageV7(params)
  } else if (shopType.isGemPagesV6) {
    return unPublishPageV6(params)
  } else if (shopType.isGemPagesMixed) {
    return unPublishPageMixed(params)
  }
}

const getPublishedPageV7 = async ({ state, type, refresh }) => {
  const { themeId, pages } = state;
  const { size, after } = pages[type];

  let themeID = themeId;
  if (!themeId) {
    themeID = await getThemeId();
    state.themeId = themeID;
  }
  const result = await pageApi.getThemePageApi({
    first: size,
    after: refresh ? undefined : after || undefined,
    themeID: themeID,
    where: {
      type: PAGE_TYPES[type],
      status: "PUBLISHED",
    }
  });
  const { pageInfo, edges } = result?.data?.themePages || {};

  state.pages[type].after = pageInfo.endCursor;
  state.pages[type].limited = !pageInfo.hasNextPage;

  const pagesData = edges.map(item => ({
    ...item.node,
    title: item.node.name,
    thumbnail_url: item.node.theme?.thumbnail,
    published: false,
    is_published: true,
    updated_at: item.node.updatedAt,
    assignmentCount: item.node.themePageOnlineStoreData?.onlineStoreObjectBaseIDs?.length || 0,
    events: [""],
    type: type === "productpages" ? "template-product" : "template-collection",
    isTemplateV7: true
  }));

  return pagesData;
};

const getPublishedPageV6 = async ({ type }) => {
  const res = await Api.base.get(ApiUrl.base[type], { params: { published: 1 } });
  return res?.data?.pages ?? [];
};

const getPublishedPageMixed = async ({ state, type, refresh }) => {
  const publishedPageV6 = await getPublishedPageV6({ state, type });
  const publishedPageV7 = await getPublishedPageV7({ state, type, refresh });
  return [...publishedPageV6, ...publishedPageV7];
};

const handleGetPublishPage = ({ state, type, refresh }) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (refresh) {
        state.pages[type].list = [];
        state.pages[type].unpublishList.v6 = [];
        state.pages[type].unpublishList.v7 = [];
      }
      const { shopInfo } = store?.state.account || {};

      const version = shopInfo?.shopType;

      const pagesData = await getPublishedPageByVersion(version, { state, type, refresh });

      for (const item of pagesData) {
        const isExisted = state.pages[type].list.find(listItem => listItem.id === item.id);
        if (!isExisted) state.pages[type].list.push(item);
      }

      resolve(pagesData);
    } catch (e) {
      // eslint-disable-next-line no-console
      reject(e);
    }
  });
};

const unPublishPageMixed = async ({ themeId, unPublishList }) => {
  let v6Response;
  let v7Response;

  const shouldUnpublish = {
    v6: unPublishList.v6.length > 0,
    v7: unPublishList.v7.length > 0
  }

  if (shouldUnpublish.v6)
    v6Response = await unPublishPageV6({ unPublishList });
  
  if (shouldUnpublish.v7)
    v7Response = await unPublishPageV7({ themeId, unPublishList });

  return {
    v7: v7Response,
    v6: v6Response
  }
}

const unPublishPageV6 = async ({ unPublishList }) => {
  const payload = {
    ids: unPublishList.v6.join(",")
  };
  if (window._webSocket && window._webSocket.connection.readyState === 1) {
    payload["isUseSocket"] = true;
  }
  const res = await Api.base.post(ApiUrl.base.downgrade.unpublish, payload);

  return res.data;
}

const unPublishPageV7 = async ({ themeId, unPublishList }) => {
  const res = await pageApi.unpublishPagesApi({
    themeId,
    ids: unPublishList.v7
  });

  return res.data;
}

const handleUnpublishPage = ({ state, pageType }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { shopInfo } = store?.state.account || {};
      const { unpublishList } = state.pages[pageType];

      const version = shopInfo?.shopType;

      const res = await unPublishedPageByVersion(version, { 
        themeId: state.themeId,
        unPublishList: unpublishList
      });

      resolve(res);

    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      reject(e);
    }
  });
};

const actions = {
  [ACTION.GET_PUBLISHED_PAGE]({ state }, { type, refresh }) {
    return handleGetPublishPage({ state, type, refresh });
  },
  [ACTION.UNPUBLISH_PAGE]({ state }, pageType) {
    return handleUnpublishPage({ state, pageType });
  },

  [ACTION.GET_PUBLISH_PAGE_COUNT]({ state }) {
    return new Promise(async (resolve, reject) => {
      try {
        const { themeId } = state;
        let themeID = themeId;
        if (!themeId) {
          themeID = await getThemeId();
          state.themeId = themeID;
        }
        const result = await pageApi.getThemePageCountApi("GEMPAGESV7");

        if (result?.data?.themePageLimitation) {
          state.pagesPublishCount = {
            publishedPagesCount: result.data.themePageLimitation.publishedPagesCount || 0,
            publishedStaticPagesCount: result.data.themePageLimitation.publishedStaticPagesCount || 0,
            publishedProductPagesCount: result.data.themePageLimitation.publishedProductPagesCount || 0,
            publishedCollectionPagesCount: result.data.themePageLimitation.publishedCollectionPagesCount || 0,
            publishedArticlePagesCount: result.data.themePageLimitation.publishedArticlePagesCount || 0,
            publishedIndexPagesCount: result.data.themePageLimitation.publishedIndexPagesCount || 0,
            maximumPublishedPages: result.data.themePageLimitation.maximumPublishedPages || 999999,
            maximumPublishedStaticPages: result.data.themePageLimitation.maximumPublishedStaticPages || 999999,
            maximumPublishedProductPages: result.data.themePageLimitation.maximumPublishedProductPages || 10,
            maximumPublishedCollectionPages: result.data.themePageLimitation.maximumPublishedCollectionPages || 999999,
            maximumPublishedArticlePages: result.data.themePageLimitation.maximumPublishedArticlePages || 20
          };
        }
        resolve(result.data.themePageLimitation);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  }
};
export default actions;
